"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import type { ResourceMetadata } from "@/lib/resources";

interface ResourceListingClientProps {
  resources: ResourceMetadata[];
  title: string;
  description: string;
  icon: React.ReactNode;
  typeLabel: string;
  slugPrefix: string;
}

export function ResourceListingClient({ 
  resources, 
  title, 
  description, 
  icon, 
  typeLabel,
  slugPrefix 
}: ResourceListingClientProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-purple-500/5 blur-[120px] -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto mb-20">
          <ScrollReveal>
            <Link 
              href="/resources" 
              className="inline-flex items-center text-base font-medium text-slate-400 hover:text-purple-400 mb-12 transition-colors group"
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Resources
            </Link>
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
              <div className="flex items-center gap-6">
                <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-purple-400">
                  {icon}
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                  {title}
                </h1>
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-72 group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
                <input 
                  type="text"
                  placeholder={`Search ${title.toLowerCase()}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-xl py-3 pl-10 pr-4 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50 transition-all"
                />
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-2xl">
              {description}
            </p>
          </ScrollReveal>
        </div>

        <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {filteredResources.length > 0 ? (
            filteredResources.map((resource, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <Link href={`/resources/${slugPrefix}/${resource.slug}`}>
                  <div className="group p-10 rounded-[2.5rem] bg-slate-900/40 border border-slate-800/50 hover:border-purple-500/40 transition-all h-full flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    
                    <div className="flex items-center justify-between mb-8">
                      <span className="text-sm font-medium text-slate-500">{resource.date}</span>
                      <span className="text-xs font-black uppercase tracking-[0.2em] text-purple-400 bg-purple-400/10 px-4 py-1.5 rounded-full border border-purple-400/20">
                        {resource.category}
                      </span>
                    </div>
                    
                    <h3 className="text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors leading-tight">
                      {resource.title}
                    </h3>
                    
                    <p className="text-slate-400 mb-10 leading-relaxed text-lg flex-grow">
                      {resource.summary}
                    </p>
                    
                    <div className="flex items-center text-base font-bold text-purple-400 group-hover:translate-x-2 transition-transform">
                      Read {typeLabel} <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))
          ) : (
            <div className="col-span-full text-center py-32 bg-slate-900/20 rounded-[3rem] border border-slate-800 border-dashed">
              <p className="text-slate-500 text-lg">
                {searchQuery ? `No ${title.toLowerCase()} match your search.` : `${title} are currently being finalized. Check back soon.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
